import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays, Calendar, Tag, Users, Waves, Sparkles } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-cabanas.jpg";
import cabanaImg from "@/assets/cabanas/cabana-riverside.jpg";
import tubingImg from "@/assets/tubing/family-tubing-fun.jpg";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";

const PUBLISH_DATE = "May 27, 2026";
const CANONICAL = "/blog/4th-of-july-guadalupe-river";
const TITLE = "4th of July on the Guadalupe — 2026 Tubing Guide";
const DESCRIPTION =
  "Plan 4th of July on the Guadalupe River near New Braunfels — cabanas, tubing, booking deadlines, pricing, fireworks tips, and how to beat the crowds.";

const FAQS = [
  {
    q: "When should I book 4th of July weekend on the Guadalupe?",
    a: "Independence Day is the single busiest weekend of the summer on the Guadalupe. We recommend booking cabanas, glamping tents, and overnight stays 6–8 weeks in advance. Day cabanas on July 3, 4, and 5 routinely sell out by mid-June.",
  },
  {
    q: "Is Son's Guadalupe open on the 4th of July?",
    a: "Yes — Son's Guadalupe is fully open July 4, 2026 for tubing, cabana rentals, glamping, and overnight camping. The river is at peak summer temperature and tubing conditions are excellent.",
  },
  {
    q: "Can you see fireworks from the Guadalupe River?",
    a: "New Braunfels hosts an official 4th of July fireworks show in town. Son's Guadalupe is a short drive from downtown — many guests grill at their cabana, then head into New Braunfels for the show. Glamping guests often enjoy a quiet riverside evening with fire pits instead.",
  },
  {
    q: "How do I avoid the 4th of July crowds on the river?",
    a: "Book a private cabana so you skip the parking-lot scramble. Arrive at 10:30 AM open, float earlier in the day, and grill lunch at your cabana instead of leaving to find food. Overnight glamping or cabin guests have the river to themselves before 10:30 AM and after 6:30 PM.",
  },
  {
    q: "Are fireworks or sparklers allowed at Son's?",
    a: "Personal fireworks, sparklers, and open burns are not permitted on the property. Texas summers are dry and fire risk is high. Designated fire pits at glamping and overnight sites are the only flames allowed.",
  },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "4th of July on the Guadalupe River — 2026 Cabanas & Tubing Guide",
    description: DESCRIPTION,
    image: "https://sonsguadalupe.com/og-image.png",
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    publisher: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      logo: { "@type": "ImageObject", url: "https://sonsguadalupe.com/og-image.png" },
    },
    datePublished: "2026-05-27",
    dateModified: "2026-05-27",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
    keywords:
      "4th of july guadalupe river, july 4th new braunfels, fourth of july tubing, independence day cabana rental, july 4th texas river",
  },
  {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "4th of July on the Guadalupe River at Son's",
    description:
      "Celebrate Independence Day on the Guadalupe River at Son's Guadalupe — private cabanas, family tubing, glamping, and riverside grilling near New Braunfels, TX.",
    startDate: "2026-07-04T10:30:00-05:00",
    endDate: "2026-07-05T18:30:00-05:00",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Son's Guadalupe",
      address: {
        "@type": "PostalAddress",
        streetAddress: "7308 River Road",
        addressLocality: "New Braunfels",
        addressRegion: "TX",
        postalCode: "78132",
        addressCountry: "US",
      },
    },
    image: "https://sonsguadalupe.com/og-image.png",
    organizer: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      url: "https://sonsguadalupe.com",
    },
    offers: {
      "@type": "AggregateOffer",
      lowPrice: 69,
      highPrice: 899,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: "https://sonsguadalupe.com/book-now",
      validFrom: "2026-05-27",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
      { "@type": "ListItem", position: 3, name: "4th of July on the Guadalupe River", item: `https://sonsguadalupe.com${CANONICAL}` },
    ],
  },
];

const BlogJuly4th2026 = () => {
  const related = getRelatedPosts("4th-of-july-guadalupe-river", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} ogImage="/og/blog-memorial-day.jpg" schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="4th of July weekend cabanas on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                4th of July on the Guadalupe River — 2026 Cabanas & Tubing Guide
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                The <strong>4th of July on the Guadalupe River</strong> is one of the biggest weekends of the Texas summer. Cool, spring-fed water, riverside grilling, kids tubing all afternoon, and fireworks just down the road in New Braunfels — this is the holiday done right. Here's how to plan a stress-free Independence Day at Son's.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Calendar className="w-5 h-5 text-teal" /> 4th of July 2026 Dates</h2>
              <p>
                Independence Day 2026 falls on a <strong>Saturday, July 4</strong>, making it a natural 3-day weekend (Fri July 3 – Sun July 5). Many guests extend through Monday July 6 for a four-day stay. Late June through early July is peak Guadalupe season — water temperatures sit in the high 70s and the river runs spring-fed clear.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Waves className="w-5 h-5 text-teal" /> What's Open at Son's on the 4th of July</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cabana rentals</strong> — all 22 private riverside cabanas open 10:30 AM – 6:30 PM</li>
                <li><strong>In-water picnic tables</strong> — riverside tables right in the shallow water</li>
                <li><strong>Tubing</strong> — heavy-duty Son's tubes, $29.99 per person ($24.99 with cabana)</li>
                <li><strong>Glamping</strong> — AC-cooled bell tents with queen beds and private fire pits</li>
                <li><strong>Tent camping & overnight stays</strong> — riverside sites at Son's River Ranch, Blue River Camp, and Rio Cibolo</li>
              </ul>

              <img src={cabanaImg} alt="4th of July cabana setup on the Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Tag className="w-5 h-5 text-teal" /> Pricing & Booking Deadlines</h2>
              <p>
                Cabana rentals start at <strong>$69</strong> and run higher on the holiday weekend due to demand. Glamping starts at <strong>$129/night</strong> and overnight cabins from <strong>$199/night</strong>. The 4th of July weekend usually sells out 4–6 weeks in advance — if you haven't booked by Memorial Day, don't wait.
              </p>
              <p>
                A 50% deposit holds your reservation; the remaining balance is due 7 days before arrival. Cancellation: more than 15 days out gets a 90% refund, 7–14 days gets 50%, inside 7 days is non-refundable.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Sparkles className="w-5 h-5 text-teal" /> Fireworks Near Son's Guadalupe</h2>
              <p>
                <strong>New Braunfels' official 4th of July fireworks show</strong> is held in town (typically at Landa Park) on the evening of July 4. Son's Guadalupe is a short drive away — most day guests pack up around 6:30 PM, grab dinner in town, and stay for the show. Overnight guests often grill at their site, light a fire pit, and enjoy a quieter riverside evening.
              </p>
              <p>
                <strong>Personal fireworks and sparklers are not allowed</strong> on the property. Texas summers are dry and the riverbank is a wildfire risk zone. Designated fire pits at glamping and overnight camping sites are the only open flames permitted.
              </p>

              <img src={tubingImg} alt="Family tubing the Guadalupe River on 4th of July weekend" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10 flex items-center gap-2"><Users className="w-5 h-5 text-teal" /> Kid-Friendly 4th of July Tips</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Book a cabana — your private shaded basecamp makes a long holiday day infinitely easier with kids</li>
                <li>Arrive at 10:30 AM open. Mid-day parking is the worst of the holiday crunch.</li>
                <li>Bring kids' life jackets (we have rentals if you forget). Required for non-swimmers.</li>
                <li>Pack red-white-and-blue swimsuits, sunscreen, and a small American flag for cabana photos</li>
                <li>Grill burgers and dogs at the cabana — way cheaper and faster than leaving for food on the busiest restaurant night of the year</li>
                <li>Reserve a glamping tent if the kids want to fall asleep to fireworks and wake up on the river</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How to Beat the 4th of July Crowds</h2>
              <p>
                The single biggest hack: <strong>book a private cabana or stay overnight.</strong> Day-trippers without reservations spend the 4th hunting for parking and standing in line at outfitters. Son's guests check in, walk to their cabana, and have river access all day. Overnight guests have the Guadalupe practically to themselves before 10:30 AM and after 6:30 PM — the best floats of the entire holiday.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">4th of July Packing Checklist</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Reef-safe sunscreen (high SPF — Texas sun is brutal on the river)</li>
                <li>Coolers with ice (no glass on the river)</li>
                <li>Life jackets for non-swimmers and kids</li>
                <li>Water shoes — the riverbed has rocks</li>
                <li>Burgers, dogs, charcoal — your cabana has a grill</li>
                <li>Bug spray for the evening</li>
                <li>Phone in a waterproof pouch for cabana photos</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">4th of July FAQ</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book 4th of July Weekend</Button></Link>
                <Link to="/cabanas"><Button variant="outline" size="lg" className="text-lg px-10 py-6">See Cabanas</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />
      </main>
      <SEOContentBlock
        eyebrow="Plan Your 4th of July"
        heading="Cabanas, Tubing & Glamping for the 4th of July on the Guadalupe"
        paragraphs={[
          "Independence Day on the Guadalupe River books up faster than any other weekend of the summer. Lock in a cabana rental on the Guadalupe River, glamping near New Braunfels, or overnight camping on River Road before the calendar fills.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogJuly4th2026;
